import React from 'react';
import {
  Calculator, BarChart3, Sparkles, Award, MessageCircle,
  TrendingUp, ArrowDown, Trophy, Target
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { cn } from '../lib/utils';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/**
 * Renders a deep breakdown of *why* a designer won the award.
 * Sections:
 *  1. Composite-score formula breakdown (line items)
 *  2. Monthly performance breakdown (table + mini bars)
 *  3. Skills excellence (6 KRA parameters)
 *  4. Approved achievements in the period
 *  5. Positive feedback comments
 *  6. Gap to runner-up
 */
export const WinnerWhyDetails = ({ winner, details, isDark }) => {
  if (!winner || !details) return null;

  const total = winner.composite_score;
  const components = details.components || [];
  const monthly = details.monthly_breakdown || [];
  const skills = details.skills || [];
  const achievements = details.achievements || [];
  const positiveComments = details.positive_comments || [];
  const gap = details.gap_to_runner_up;

  return (
    <div className="space-y-6" data-testid="winner-why-details">
      {/* 1. COMPOSITE SCORE BREAKDOWN */}
      <Card className={cn('border-0 shadow-sm', isDark ? 'bg-slate-800/50' : 'bg-white')}>
        <CardHeader className="pb-3">
          <CardTitle className={cn('flex items-center gap-2 text-base font-outfit', isDark ? 'text-white' : 'text-slate-800')}>
            <Calculator className="w-4 h-4 text-[#331062] dark:text-purple-400" />
            Why {winner.designer_name?.split(' ')[0]} Won — Composite Score Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={cn('text-left text-xs uppercase tracking-wider', isDark ? 'text-slate-400' : 'text-slate-500')}>
                  <th className="pb-2 pl-2">Component</th>
                  <th className="pb-2 text-right">Raw Value</th>
                  <th className="pb-2 text-right">Weight</th>
                  <th className="pb-2 text-right pr-2">Contribution</th>
                </tr>
              </thead>
              <tbody className={isDark ? 'divide-y divide-slate-700' : 'divide-y divide-slate-100'}>
                {components.map((c) => {
                  const isAdd = c.sign === '+';
                  const value = isAdd ? c.contribution : -c.contribution;
                  return (
                    <tr key={c.label}>
                      <td className="py-2 pl-2 font-medium">
                        <span className={cn(
                          'inline-flex items-center gap-1.5'
                        )}>
                          <span className={cn(
                            'w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center',
                            isAdd ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                          )}>{c.sign}</span>
                          {c.label}
                        </span>
                      </td>
                      <td className="py-2 text-right">{c.value}</td>
                      <td className="py-2 text-right">× {c.weight}</td>
                      <td className={cn(
                        'py-2 text-right pr-2 font-bold',
                        isAdd ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'
                      )}>
                        {isAdd ? '+' : '−'}{Math.abs(c.contribution).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
                <tr className={cn(isDark ? 'bg-purple-900/30' : 'bg-purple-50')}>
                  <td className="py-3 pl-2 font-bold text-[#331062] dark:text-purple-300" colSpan={3}>
                    = Final Composite Score
                  </td>
                  <td className="py-3 text-right pr-2 font-bold text-xl text-[#331062] dark:text-purple-300">
                    {total}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 2. MONTHLY BREAKDOWN */}
        <Card className={cn('border-0 shadow-sm', isDark ? 'bg-slate-800/50' : 'bg-white')}>
          <CardHeader className="pb-3">
            <CardTitle className={cn('flex items-center gap-2 text-base font-outfit', isDark ? 'text-white' : 'text-slate-800')}>
              <TrendingUp className="w-4 h-4 text-blue-500" />
              Performance Over the Quarter
            </CardTitle>
          </CardHeader>
          <CardContent>
            {monthly.length === 0 ? (
              <p className="text-sm text-slate-500">No scoring data available.</p>
            ) : (
              <div className="space-y-3">
                {monthly.map((m) => (
                  <div key={`${m.year}-${m.month}`} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className={cn('font-semibold', isDark ? 'text-white' : 'text-slate-800')}>
                        {MONTHS[m.month - 1]} {m.year}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {m.weeks} weeks · +{m.positive_feedback} feedback · {m.escalations} escalations
                      </span>
                    </div>
                    <div className="flex gap-2 items-center">
                      <div className="flex-1 h-2.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#331062] to-purple-500 rounded-full transition-all"
                          style={{ width: `${Math.min(100, m.avg_performance)}%` }}
                        />
                      </div>
                      <span className={cn('text-sm font-bold w-12 text-right', isDark ? 'text-white' : 'text-slate-800')}>
                        {m.avg_performance}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 3. SKILLS BREAKDOWN */}
        <Card className={cn('border-0 shadow-sm', isDark ? 'bg-slate-800/50' : 'bg-white')}>
          <CardHeader className="pb-3">
            <CardTitle className={cn('flex items-center gap-2 text-base font-outfit', isDark ? 'text-white' : 'text-slate-800')}>
              <Sparkles className="w-4 h-4 text-amber-500" />
              Skills Excellence (KRA 1-5)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {skills.map((s) => {
                const pct = (s.value / 5) * 100;
                const color =
                  s.value >= 4.3 ? 'bg-emerald-500'
                    : s.value >= 3.7 ? 'bg-blue-500'
                    : s.value >= 3 ? 'bg-amber-500'
                    : 'bg-red-500';
                return (
                  <div key={s.name} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className={cn('font-medium', isDark ? 'text-slate-300' : 'text-slate-700')}>
                        {s.name}
                      </span>
                      <span className={cn('text-sm font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                        {s.value.toFixed(2)}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                      <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 4. ACHIEVEMENTS */}
      {achievements.length > 0 && (
        <Card className={cn('border-0 shadow-sm', isDark ? 'bg-slate-800/50' : 'bg-white')}>
          <CardHeader className="pb-3">
            <CardTitle className={cn('flex items-center gap-2 text-base font-outfit', isDark ? 'text-white' : 'text-slate-800')}>
              <Award className="w-4 h-4 text-amber-500" />
              Approved Achievements ({achievements.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {achievements.map((a, i) => (
                <div
                  key={i}
                  className={cn(
                    'rounded-lg p-3 border-l-4 border-amber-400',
                    isDark ? 'bg-slate-900/40' : 'bg-amber-50/40'
                  )}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h4 className={cn('font-semibold text-sm', isDark ? 'text-white' : 'text-slate-800')}>
                      {a.title}
                    </h4>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500 flex-shrink-0">
                      {a.date_achieved}
                    </span>
                  </div>
                  {a.category && (
                    <span className="inline-block text-[10px] uppercase tracking-wider font-bold text-purple-600 dark:text-purple-400 mb-1">
                      {String(a.category).replace(/_/g, ' ')}
                    </span>
                  )}
                  <p className={cn('text-xs leading-relaxed', isDark ? 'text-slate-400' : 'text-slate-600')}>
                    {a.description}
                  </p>
                  {a.impact && (
                    <p className="text-xs mt-2 text-emerald-700 dark:text-emerald-400 italic">
                      <span className="font-semibold not-italic">Impact:</span> {a.impact}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 5. POSITIVE FEEDBACK COMMENTS */}
      {positiveComments.length > 0 && (
        <Card className={cn('border-0 shadow-sm', isDark ? 'bg-slate-800/50' : 'bg-white')}>
          <CardHeader className="pb-3">
            <CardTitle className={cn('flex items-center gap-2 text-base font-outfit', isDark ? 'text-white' : 'text-slate-800')}>
              <MessageCircle className="w-4 h-4 text-emerald-500" />
              Manager Recognition ({positiveComments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {positiveComments.map((c, i) => (
                <div
                  key={i}
                  className={cn(
                    'rounded-lg p-3 flex gap-3 items-start border-l-4 border-emerald-500',
                    isDark ? 'bg-emerald-900/10' : 'bg-emerald-50/40'
                  )}
                >
                  <span className={cn(
                    'flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider',
                    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                  )}>
                    {MONTHS[c.month - 1]} {c.year} · W{c.week}
                  </span>
                  <p className={cn('text-sm flex-1', isDark ? 'text-slate-300' : 'text-slate-700')}>
                    "{c.comment}"
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 6. GAP TO RUNNER-UP */}
      {gap && (
        <Card className={cn('border-0 shadow-sm', isDark ? 'bg-slate-800/50' : 'bg-white')}>
          <CardHeader className="pb-3">
            <CardTitle className={cn('flex items-center gap-2 text-base font-outfit', isDark ? 'text-white' : 'text-slate-800')}>
              <Trophy className="w-4 h-4 text-yellow-500" />
              How {winner.designer_name?.split(' ')[0]} Beat the Runner-Up
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row items-stretch gap-4">
              {/* Winner */}
              <div className={cn(
                'flex-1 rounded-xl p-4 border-2',
                'border-yellow-400 bg-yellow-50/40 dark:bg-yellow-900/10'
              )}>
                <div className="text-[10px] uppercase tracking-wider font-bold text-yellow-700 dark:text-yellow-400 mb-1">
                  🥇 Winner
                </div>
                <div className={cn('font-bold text-lg', isDark ? 'text-white' : 'text-slate-800')}>
                  {winner.designer_name}
                </div>
                <div className="text-xs text-slate-500 mb-2">{winner.employee_id}</div>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-yellow-700 dark:text-yellow-400">{total}</span>
                  <span className="text-xs text-slate-500">composite</span>
                </div>
              </div>

              {/* Gap arrow */}
              <div className="flex flex-col items-center justify-center px-4 gap-1">
                <ArrowDown className="w-6 h-6 text-slate-400" />
                <div className={cn(
                  'text-3xl font-bold',
                  gap.gap >= 5 ? 'text-emerald-600' : gap.gap >= 2 ? 'text-blue-600' : 'text-amber-600'
                )}>
                  +{gap.gap}
                </div>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Lead
                </span>
              </div>

              {/* Runner-up */}
              <div className={cn(
                'flex-1 rounded-xl p-4 border',
                isDark ? 'border-slate-700 bg-slate-900/40' : 'border-slate-200 bg-slate-50/40'
              )}>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                  🥈 Runner-Up
                </div>
                <div className={cn('font-bold text-lg', isDark ? 'text-white' : 'text-slate-800')}>
                  {gap.name}
                </div>
                <div className="text-xs text-slate-500 mb-2">{gap.employee_id}</div>
                <div className="flex items-baseline gap-1">
                  <span className={cn('text-3xl font-bold', isDark ? 'text-slate-300' : 'text-slate-600')}>
                    {gap.composite_score}
                  </span>
                  <span className="text-xs text-slate-500">composite</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Eligibility note */}
      <div className={cn(
        'flex items-start gap-2 p-3 rounded-lg text-xs',
        isDark ? 'bg-slate-700/40 text-slate-400' : 'bg-slate-50 text-slate-600'
      )}>
        <Target className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          Eligibility: must have been scored at least 2 weeks per month in the period.
          Composite formula: <strong>avg_ranking + 1.5 × positive_feedback + 2 × achievements − 2 × minor_escalations − 5 × major_escalations</strong>
        </span>
      </div>
    </div>
  );
};

export default WinnerWhyDetails;
